<template>
  <ModalDialog ref="modal" data-testid="lead-note-dialog" persistent max-width="896px" :title="t('LeadDetail.notes.dialogTitle')">
    <p class="mb-6 txt-body-s text-subtle" data-testid="lead-note-description">{{ t('LeadDetail.notes.description') }}</p>

    <TextareaField
      id="lead-note-input"
      ref="noteInput"
      v-model="draft"
      rows="5"
      resize="vertical"
      :placeholder="t('LeadDetail.notes.placeholder')"
      data-testid="lead-note-input"
    />

    <div class="mt-6 flex justify-end gap-3">
      <button type="button" class="btn-outline-neutral-s" data-testid="lead-note-cancel" @click="close">
        {{ t('common.cancel') }}
      </button>
      <button
        type="button"
        class="btn-primary-color-s"
        :class="{ 'btn-disabled': !canSubmit }"
        :disabled="!canSubmit"
        data-testid="lead-note-submit"
        @click="onSubmit"
      >
        {{ t('LeadDetail.notes.submit') }}
      </button>
    </div>
  </ModalDialog>
</template>

<script setup lang="ts">
import { ModalDialog, TextareaField } from '@ramp106/omrjs-core-ui'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { computed, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useAddReviewsLeadNoteMutation } from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'

const props = defineProps<{
  leadId: string
}>()

const { t } = useI18n()

const modal = ref<InstanceType<typeof ModalDialog> | null>(null)
const noteInput = ref<InstanceType<typeof TextareaField> | null>(null)
const draft = ref('')

const { mutate, loading } = useAddReviewsLeadNoteMutation({
  clientId: 'reviews',
  refetchQueries: ['reviewsLead'],
  awaitRefetchQueries: true,
})

const canSubmit = computed(() => draft.value.trim().length > 0 && !loading.value)

// the textarea only exists once ModalDialog has mounted its body
function open() {
  draft.value = ''
  modal.value?.open()
  nextTick(() => noteInput.value?.inputRef?.focus())
}

function close() {
  modal.value?.close()
}

async function onSubmit() {
  if (!canSubmit.value) return

  track('cta_click', { surface: 'cta', category: 'meaningful_action', cta_id: 'lead_note_add', cta_position: 'lead_detail' })

  try {
    await mutate({ leadId: props.leadId, body: draft.value.trim() })
    draft.value = ''
    close()
  } catch {
    showNotification(t('LeadDetail.notes.errorNotification'), 'error', 10000)
  }
}

defineExpose({ open, close })
</script>
